/**
 * Tiny fetch wrapper:
 *   - Adds Authorization: Bearer <access_token>
 *   - On 401 with `auth/token-expired` (or generic expired): triggers refresh
 *     once and replays the request transparently.
 *   - On every error: parses RFC 7807 body and throws a typed `ApiError`.
 *
 * Auth-store coupling is done via a lazy importer to avoid an import cycle
 * (the store imports from `./client` too).
 */

import type { ProblemDetail } from './types';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetail;
  constructor(status: number, problem: ProblemDetail, message?: string) {
    super(message ?? problem.detail ?? problem.title ?? `API ${status}`);
    this.status = status;
    this.problem = problem;
    this.name = 'ApiError';
  }
  get code(): string | undefined {
    return this.problem.code;
  }
  isAuthExpired(): boolean {
    return (
      this.status === 401 &&
      (this.problem.code === 'auth/token-expired' ||
        this.problem.code === 'auth_token_expired' ||
        this.problem.code === 'token_expired')
    );
  }
  isForbidden(): boolean {
    return this.status === 403;
  }
}

/* ---- Auth-store accessor (lazy to break cycle) ---------------------------- */

type AuthStoreLike = {
  accessToken: string | null;
  refresh: () => Promise<string | null>;
  forceLogout: (reason?: string) => void;
};
let resolveAuthStore: (() => AuthStoreLike | null) | null = null;

export function registerAuthStoreAccessor(resolver: () => AuthStoreLike | null) {
  resolveAuthStore = resolver;
}

function authStore(): AuthStoreLike | null {
  return resolveAuthStore ? resolveAuthStore() : null;
}

/* ---- Core request --------------------------------------------------------- */

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip Authorization header (used by /auth/login + /auth/refresh). */
  anonymous?: boolean;
  /** Internal flag — set true after one auto-refresh to avoid loops. */
  _retry?: boolean;
  signal?: AbortSignal;
}

export async function apiRequest<T>(req: ApiRequest): Promise<T> {
  const url = buildUrl(req.path, req.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(req.headers ?? {}),
  };
  if (req.body !== undefined) headers['Content-Type'] = 'application/json';

  if (!req.anonymous) {
    const store = authStore();
    if (store?.accessToken) {
      headers['Authorization'] = `Bearer ${store.accessToken}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: req.method ?? 'GET',
      headers,
      body: req.body === undefined ? undefined : JSON.stringify(req.body),
      credentials: 'omit',
      signal: req.signal,
    });
  } catch (err) {
    throw new ApiError(0, {
      title: 'Netzwerkfehler',
      detail: (err as Error).message,
      code: 'network_error',
    });
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const ct = response.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json') || ct.includes('application/problem+json');

  if (!response.ok) {
    const problem: ProblemDetail = isJson
      ? await response.json().catch(() => ({}))
      : { detail: await response.text().catch(() => '') };
    const apiErr = new ApiError(response.status, problem);

    // 401 + token-expired => single refresh attempt
    if (apiErr.isAuthExpired() && !req._retry && !req.anonymous) {
      const store = authStore();
      if (store) {
        try {
          const newToken = await store.refresh();
          if (newToken) {
            return apiRequest<T>({ ...req, _retry: true });
          }
        } catch {
          // refresh threw → fall through to logout
        }
        store.forceLogout('session_expired');
      }
    }

    if (apiErr.isForbidden()) {
      // RBAC tag for grep/log filtering — server is authoritative, UI hides
      // for UX only. See Spec §5.1.

      console.warn('[rbac-denied]', {
        path: req.path,
        method: req.method ?? 'GET',
        code: apiErr.code,
        missing_permissions: apiErr.problem.missing_permissions,
      });
    }
    throw apiErr;
  }

  if (!isJson) return undefined as unknown as T;
  return (await response.json()) as T;
}

function buildUrl(path: string, query?: ApiRequest['query']): string {
  const base = API_BASE.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  let url = `${base}${cleanPath}`;
  if (query && Object.keys(query).length > 0) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      params.append(k, String(v));
    }
    const s = params.toString();
    if (s) url += `?${s}`;
  }
  return url;
}

export const api = {
  get: <T>(path: string, query?: ApiRequest['query'], anonymous = false) =>
    apiRequest<T>({ method: 'GET', path, query, anonymous }),
  post: <T>(path: string, body?: unknown, anonymous = false) =>
    apiRequest<T>({ method: 'POST', path, body, anonymous }),
  patch: <T>(path: string, body?: unknown, anonymous = false) =>
    apiRequest<T>({ method: 'PATCH', path, body, anonymous }),
  delete: <T>(path: string, anonymous = false) =>
    apiRequest<T>({ method: 'DELETE', path, anonymous }),
};
